import React from "react";
import { Box, Checkbox, FormControlLabel, Stack } from "@mui/material";
import type {
  SpecType,
  SpecsValidationIssue,
} from "../../../service/SpecsRevisionService";
import {
  LayerColorPicker,
  SpecsInputField,
  SpecsImageUrlField,
  SpecsAutocompleteField,
  SpecsMultiAutocompleteField,
  SpecsSelectField,
} from "../SpecsEditorFields/index";
import { logSpecsModelPath } from "../specsModelPathLogger";
import { getAutoFormFieldMetadata } from "../specsModel/autoFormMetadata";
import { formatSpecsInheritedPlaceholder } from "../specsInheritedPlaceholder";
import type { ObservableModelRoot } from "../specsModel/observable";
import type { SpecsImageUploadContext } from "../specsImageUpload";
import {
  getValueAtModelPath,
  joinModelPath,
  setValueAtModelPath,
} from "./modelPath";
import SpecsArraySection from "./SpecsArraySection";
import SpecsDeliveryTimeField from "./SpecsDeliveryTimeField";
import SpecsJsonField from "./SpecsJsonField";
import SpecsNumberField from "./SpecsNumberField";
import SpecsObjectSection from "./SpecsObjectSection";
import SpecsPricingField from "./SpecsPricingField";
import { SpecsValidationIssueShell } from "./SpecsValidationIssueHighlight";
import type { SpecsFormEntry, SpecsFormFieldSchema } from "./types";
import { getSpecsIssueMatchForModelPath } from "../specsValidationIssues";

function normalizeColorValue(value: unknown) {
  return typeof value === "string" && value.trim() ? value : "#000000";
}

function splitFieldPath(path: string) {
  const segments = path.split(".");
  const key = segments.pop();

  if (!key) {
    throw new Error(`Invalid form field path "${path}"`);
  }

  return {
    parentPath: segments.join("."),
    key,
  };
}

function formatFieldLabel(key: string) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (char) => char.toUpperCase());
}

function SpecsFormField({
  model,
  path,
  field,
  debugPathPrefix,
  clickToEditFields,
  imageUploadContext,
  validationIssues = [],
  validationSpecType,
}: {
  model: ObservableModelRoot;
  path: string;
  field: SpecsFormFieldSchema;
  debugPathPrefix?: string;
  clickToEditFields?: boolean;
  imageUploadContext?: Omit<SpecsImageUploadContext, "fieldPath">;
  validationIssues?: SpecsValidationIssue[];
  validationSpecType?: SpecType;
}) {
  const value = getValueAtModelPath(model, path);
  const { parentPath, key } = splitFieldPath(path);
  const parentValue = parentPath
    ? getValueAtModelPath(model, parentPath)
    : model;
  const metadata = getAutoFormFieldMetadata(parentValue, key);
  const label = field.label ?? metadata?.label ?? formatFieldLabel(key);
  const fieldType = field.fieldType ?? metadata?.fieldType ?? "text";
  const helperText = field.helperText ?? metadata?.helperText;
  const placeholder = formatSpecsInheritedPlaceholder(
    field.placeholder ?? metadata?.placeholder
  );
  const minRows = field.minRows ?? metadata?.minRows;
  const options = field.options ?? metadata?.options;
  const allowNull = field.allowNull ?? metadata?.allowNull;
  const debugPath =
    metadata?.debugPath ??
    (debugPathPrefix
      ? `${debugPathPrefix}.${path}`
      : `window.specs.model.${path}`);
  const issueMatch = validationSpecType
    ? getSpecsIssueMatchForModelPath({
        issues: validationIssues,
        specType: validationSpecType,
        modelPath: path,
      })
    : null;
  const wrapField = (fieldNode: React.ReactNode) => (
    <SpecsValidationIssueShell match={issueMatch}>
      {fieldNode}
    </SpecsValidationIssueShell>
  );

  if (fieldType === "boolean") {
    return wrapField(
      <FormControlLabel
        control={
          <Checkbox
            checked={Boolean(value)}
            onChange={(_, checked) => setValueAtModelPath(model, path, checked)}
          />
        }
        label={
          <Box
            component="span"
            onClick={() => logSpecsModelPath(debugPath)}
            sx={{ cursor: "pointer" }}
          >
            {label}
          </Box>
        }
      />
    );
  }

  if (fieldType === "stringList") {
    return wrapField(
      <SpecsMultiAutocompleteField
        label={label}
        value={Array.isArray(value) ? (value as string[]) : []}
        options={(options ?? []).map((option) => option.value)}
        placeholder={placeholder}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  if (fieldType === "select") {
    return wrapField(
      <SpecsSelectField
        label={label}
        value={typeof value === "string" ? value : ""}
        options={options ?? []}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  if (fieldType === "autocomplete") {
    return wrapField(
      <SpecsAutocompleteField
        label={label}
        value={typeof value === "string" ? value : ""}
        options={(options ?? []).map((option) => option.value)}
        placeholder={placeholder}
        debugPath={debugPath}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  if (fieldType === "imageUrl") {
    return wrapField(
      <SpecsImageUrlField
        label={label}
        value={typeof value === "string" ? value : ""}
        placeholder={placeholder}
        helperText={helperText}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        accept={metadata?.imageUpload?.accept}
        uploadValidation={metadata?.imageUpload?.validation}
        uploadContext={{
          ...imageUploadContext,
          fieldPath: path,
        }}
        onChange={(event) =>
          setValueAtModelPath(model, path, event.target.value || "")
        }
      />
    );
  }

  if (fieldType === "color") {
    return wrapField(
      <Stack spacing={1}>
        <SpecsInputField
          label={label}
          value={typeof value === "string" ? value : ""}
          placeholder={placeholder}
          helperText={helperText}
          debugPath={debugPath}
          clickToEdit={clickToEditFields}
          onChange={(event) =>
            setValueAtModelPath(model, path, event.target.value || "")
          }
        />
        <LayerColorPicker
          color={normalizeColorValue(value)}
          onChange={(hex) => setValueAtModelPath(model, path, hex)}
        />
      </Stack>
    );
  }

  if (fieldType === "number") {
    return wrapField(
      <SpecsNumberField
        label={label}
        value={typeof value === "number" ? value : null}
        helperText={helperText}
        placeholder={placeholder}
        allowNull={allowNull}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  if (fieldType === "json") {
    return wrapField(
      <SpecsJsonField
        label={label}
        value={value}
        minRows={minRows}
        helperText={helperText}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  if (fieldType === "deliveryTime") {
    return wrapField(
      <SpecsDeliveryTimeField
        label={label}
        value={value}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  if (fieldType === "pricing") {
    return wrapField(
      <SpecsPricingField
        label={label}
        value={value}
        debugPath={debugPath}
        clickToEdit={clickToEditFields}
        onChange={(nextValue) => setValueAtModelPath(model, path, nextValue)}
      />
    );
  }

  return wrapField(
    <SpecsInputField
      label={label}
      value={typeof value === "string" ? value : ""}
      placeholder={placeholder}
      helperText={helperText}
      debugPath={debugPath}
      clickToEdit={clickToEditFields}
      onChange={(event) => setValueAtModelPath(model, path, event.target.value)}
    />
  );
}

export default function SpecsFormEntryRenderer({
  model,
  entry,
  basePath,
  debugPathPrefix,
  clickToEditFields,
  imageUploadContext,
  validationIssues,
  validationSpecType,
}: {
  model: ObservableModelRoot;
  entry: SpecsFormEntry;
  basePath: string;
  debugPathPrefix?: string;
  clickToEditFields?: boolean;
  imageUploadContext?: Omit<SpecsImageUploadContext, "fieldPath">;
  validationIssues?: SpecsValidationIssue[];
  validationSpecType?: SpecType;
}) {
  if (entry.kind === "field") {
    return (
      <SpecsFormField
        model={model}
        path={joinModelPath(basePath, entry.path)}
        field={entry}
        debugPathPrefix={debugPathPrefix}
        clickToEditFields={clickToEditFields}
        imageUploadContext={imageUploadContext}
        validationIssues={validationIssues}
        validationSpecType={validationSpecType}
      />
    );
  }

  if (entry.kind === "object") {
    return (
      <SpecsObjectSection
        model={model}
        entry={entry}
        basePath={basePath}
        validationIssues={validationIssues}
        validationSpecType={validationSpecType}
        renderEntry={(childEntry, childBasePath) => (
          <SpecsFormEntryRenderer
            model={model}
            entry={childEntry}
            basePath={childBasePath}
            debugPathPrefix={debugPathPrefix}
            clickToEditFields={clickToEditFields}
            imageUploadContext={imageUploadContext}
            validationIssues={validationIssues}
            validationSpecType={validationSpecType}
          />
        )}
      />
    );
  }

  return (
    <SpecsArraySection
      model={model}
      entry={entry}
      basePath={basePath}
      validationIssues={validationIssues}
      validationSpecType={validationSpecType}
      renderEntry={(childEntry, childBasePath) => (
        <SpecsFormEntryRenderer
          model={model}
          entry={childEntry}
          basePath={childBasePath}
          debugPathPrefix={debugPathPrefix}
          clickToEditFields={clickToEditFields}
          imageUploadContext={imageUploadContext}
          validationIssues={validationIssues}
          validationSpecType={validationSpecType}
        />
      )}
    />
  );
}
